import { HTMLAttributes, FC, SVGProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";

const badgeVariants = cva(
  "inline-flex items-center rounded-full text-sm font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-slate-950 focus:ring-offset-2",
  {
    variants: {
      variant: {
        primary: "bg-primary-50 text-black",
        secondary: "bg-pink-50 text-black",
        dark: "bg-slate-900 text-slate-50",
        light: "bg-neutral-300 text-black",
        error: "bg-pink-50 text-pink-500 fill-pink-500",
        white: "border border-slate-200 bg-white text-white",
        lightGreen: "bg-green-50 text-green-500 fill-green-500",
      },
      hover: {
        primary: "hover:bg-primary-100-600/80 cursor-pointer",
        secondary: "hover:bg-pink-100/80 cursor-pointer",
        dark: "hover:bg-slate-900/80 cursor-pointer",
        light: "hover:bg-slate-100/80 cursor-pointer",
        error: "hover:bg-red-500/80 cursor-pointer",
        white: "hover:bg-slate-100/80 cursor-pointer",
        lightGreen: "hover:bg-green-100-100/80 cursor-pointer",
      },
      size: {
        xs: "text-xs",
        sm: "text-sm",
        base: "text-md",
      },
    },
    defaultVariants: { variant: "primary" },
  },
);

const labelVariants: Record<
  NonNullable<VariantProps<typeof badgeVariants>["variant"]>,
  string
> = {
  primary: "text-black",
  secondary: "text-black",
  dark: "text-slate-50",
  light: "text-black",
  error: "text-black",
  white: "text-black",
  lightGreen: "text-black",
};

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    Pick<VariantProps<typeof badgeVariants>, "variant" | "size"> {
  /**
   * If `true`, the badge will be styled as a hover badge
   * that changes color on hover and pointer cursor.
   */
  hover?: boolean;
  label?: string;
  icon?: FC<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  iconSpin?: boolean;
  iconFill?: string;
}

export const Badge = ({
  className,
  variant,
  size = "sm",
  icon: Icon,
  iconClassName,
  iconSpin = false,
  iconFill,
  label,
  hover = false,
  ...props
}: BadgeProps) => (
  <div
    className={cn(
      "flex items-center gap-1 border-transparent",
      Icon ? "py-0.5 pl-1.5 pr-1" : "px-1.5 py-0.5",
      badgeVariants({ variant, size, hover: hover ? variant : null }),
      className,
    )}
    {...props}
  >
    {label && (
      <Typography
        weight="semibold"
        size={size ? size : "sm"}
        truncate
        className={variant ? labelVariants[variant] : undefined}
      >
        {label}
      </Typography>
    )}
    {Icon && (
      <Icon
        width={16}
        height={16}
        fill={iconFill}
        className={cn(iconSpin ? "duration animate-spin" : "", iconClassName)}
        style={{ msTransitionDuration: "2000ms", animationDuration: "2s" }}
      />
    )}
  </div>
);
